// E8SavingsBarChartClosedTask.js
// Zadanie zamknięte E8 – odczytywanie danych z poziomego diagramu słupkowego

import React from "react";
import { ChoiceOptionMath, InlineMath } from "../../../../components/math/KatexWrappers";
import ABCDInput from "../../../../components/inputs/ABCDInput";

// ============================================================
// OSOBY
// ============================================================

const PERSONS = [
  { name: "Aldona",  verb: "odłożyła", genitive: "Aldony"   },
  { name: "Kasia",   verb: "odłożyła", genitive: "Kasi"     },
  { name: "Zosia",   verb: "odłożyła", genitive: "Zosi"     },
  { name: "Marta",   verb: "odłożyła", genitive: "Marty"    },
  { name: "Maja",    verb: "odłożyła", genitive: "Mai"      },
  { name: "Marek",   verb: "odłożył",  genitive: "Marka"    },
  { name: "Bartek",  verb: "odłożył",  genitive: "Bartka"   },
  { name: "Tomek",   verb: "odłożył",  genitive: "Tomka"    },
  { name: "Łukasz",  verb: "odłożył",  genitive: "Łukasza"  },
  { name: "Kamil",   verb: "odłożył",  genitive: "Kamila"   },
];

// ============================================================
// PRODUKTY
// ============================================================

const PRODUCTS = [
  { nom: "Deskorolka",            acc: "deskorolkę",           gen: "deskorolki",           price: 180 },
  { nom: "Rower",                 acc: "rower",                gen: "roweru",               price: 240 },
  { nom: "Aparat fotograficzny",  acc: "aparat fotograficzny", gen: "aparatu fotograficznego", price: 300 },
  { nom: "Plecak",                acc: "plecak",               gen: "plecaka",              price: 120 },
  { nom: "Słuchawki",             acc: "słuchawki",            gen: "słuchawek",            price: 200 },
];

const MONTH_NOM = [
  "styczeń","luty","marzec","kwiecień","maj","czerwiec",
  "lipiec","sierpień","wrzesień","październik","listopad","grudzień",
];
const MONTH_LOC = [
  "styczniu","lutym","marcu","kwietniu","maju","czerwcu",
  "lipcu","sierpniu","wrześniu","październiku","listopadzie","grudniu",
];

// ładne ułamki dla pyt. 1: suma dwóch miesięcy = frac * cena
const Q1_FRACS = [
  { pct: 25, num: 1, den: 4 },
  { pct: 40, num: 2, den: 5 },
  { pct: 50, num: 1, den: 2 },
  { pct: 60, num: 3, den: 5 },
  { pct: 75, num: 3, den: 4 },
];

// procenty dla pyt. 2: o ile % miesiąc droższy od innego
const Q2_PCTS = [10, 20, 25, 50];

// ============================================================
// HELPERS
// ============================================================

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ============================================================
// GENERATOR
// ============================================================

function createOriginalTask() {
  return {
    type: "E8SavingsBarChartClosedTask",
    data: {
      person: PERSONS[0],
      productNom: "Deskorolka",
      productAcc: "deskorolkę",
      productGen: "deskorolki",
      price: 180,
      monthIndices: [3, 2, 1, 0],
      monthValues: [30, 60, 40, 50],
      q1Slots: [3, 2],
      q2HiSlot: 1,
      q2LoSlot: 3,
      q1Pct: 50,
      q1Distractor: 45,
      q2Pct: 20,
      q2Distractor: 10,
      q1CorrectFirst: false,
      q2CorrectFirst: false,
    },
    source: {
      variant: "original",
      exam: "E8",
      session: "2025_MAJ",
      taskNumber: 1,
    },
  };
}

export function generateTask(options = {}) {
  if (options.variant === "original") return createOriginalTask();

  for (let attempt = 0; attempt < 80; attempt++) {
    const product = pick(PRODUCTS);
    const P = product.price;
    const person = pick(PERSONS);

    const frac = pick(Q1_FRACS);
    const q1Sum = (P * frac.num) / frac.den;
    if (!Number.isInteger(q1Sum)) continue;

    const q1Splits = [];
    for (let a = 10; a < q1Sum; a += 10) {
      const b = q1Sum - a;
      if (b >= 10 && a !== b) q1Splits.push([a, b]);
    }
    if (!q1Splits.length) continue;
    const [v1, v2] = pick(q1Splits);

    const q2Pct = pick(Q2_PCTS);
    const q2Candidates = [];
    for (let b = 20; b <= 70; b += 10) {
      const h = (b * (100 + q2Pct)) / 100;
      if (Number.isInteger(h) && h !== b && h <= 90) {
        q2Candidates.push({ base: b, higher: h });
      }
    }
    if (!q2Candidates.length) continue;
    const { base: q2Base, higher: q2Higher } = pick(q2Candidates);

    const startMonth = Math.floor(Math.random() * 9);
    const monthIndices = [startMonth, startMonth + 1, startMonth + 2, startMonth + 3];

    const slots = shuffle([
      { role: "q1a", value: v1 },
      { role: "q1b", value: v2 },
      { role: "q2hi", value: q2Higher },
      { role: "q2lo", value: q2Base },
    ]);

    const monthValues = slots.map((s) => s.value);
    const q1Slots = [
      slots.findIndex((s) => s.role === "q1a"),
      slots.findIndex((s) => s.role === "q1b"),
    ].sort((a, b) => a - b);
    const q2HiSlot = slots.findIndex((s) => s.role === "q2hi");
    const q2LoSlot = slots.findIndex((s) => s.role === "q2lo");

    const q1Distractor = pick(Q1_FRACS.filter((f) => f.pct !== frac.pct)).pct;
    const q2Distractor = pick(Q2_PCTS.filter((p) => p !== q2Pct));

    // true = poprawna jest pierwsza opcja (A lub C)
    const q1CorrectFirst = Math.random() < 0.5;
    const q2CorrectFirst = Math.random() < 0.5;

    return {
      type: "E8SavingsBarChartClosedTask",
      data: {
        person,
        productNom: product.nom,
        productAcc: product.acc,
        productGen: product.gen,
        price: P,
        monthIndices,
        monthValues,
        q1Slots,
        q2HiSlot,
        q2LoSlot,
        q1Pct: frac.pct,
        q1Distractor,
        q2Pct,
        q2Distractor,
        q1CorrectFirst,
        q2CorrectFirst,
      },
      source: { variant: "random", basedOn: "E8_2025_MAJ_1" },
    };
  }

  return {
    ...createOriginalTask(),
    source: { variant: "random", basedOn: "E8_2025_MAJ_1" },
  };
}

// ============================================================
// DIAGRAM – poziomy wykres słupkowy (SVG, kolor motywu)
// ============================================================

function HorizontalBarChart({ monthIndices, monthValues }) {
  const maxVal = Math.max(...monthValues);
  const step = maxVal <= 60 ? 10 : 20;
  const axisMax = Math.ceil(maxVal / step) * step;

  const svgW = 380;
  const rowH = 44;
  const barH = 26;
  const leftPad = 90;
  const rightPad = 28;
  const topPad = 8;
  const bottomPad = 36;
  const svgH = monthIndices.length * rowH + topPad + bottomPad;
  const chartW = svgW - leftPad - rightPad;

  const ticks = [];
  for (let v = 0; v <= axisMax; v += step) ticks.push(v);

  return (
    <svg
      width={svgW}
      height={svgH}
      style={{ display: "block", margin: "12px auto", overflow: "visible" }}
      aria-label="Diagram słupkowy oszczędności"
    >
      {/* siatka pionowa */}
      {ticks.map((v) => {
        const x = leftPad + (v / axisMax) * chartW;
        return (
          <g key={v}>
            <line
              x1={x} y1={topPad}
              x2={x} y2={svgH - bottomPad}
              stroke="currentColor" strokeOpacity="0.15" strokeWidth="1"
            />
            <text
              x={x} y={svgH - bottomPad + 14}
              textAnchor="middle" fontSize="11"
              fill="currentColor" opacity="0.8"
            >
              {v}
            </text>
          </g>
        );
      })}

      {/* oś bazowa */}
      <line
        x1={leftPad} y1={topPad}
        x2={leftPad} y2={svgH - bottomPad}
        stroke="currentColor" strokeOpacity="0.4" strokeWidth="1"
      />

      {/* słupki */}
      {monthIndices.map((mIdx, i) => {
        const val = monthValues[i];
        const barW = (val / axisMax) * chartW;
        const y = topPad + i * rowH;
        return (
          <g key={mIdx}>
            {/* etykieta miesiąca */}
            <text
              x={leftPad - 8} y={y + barH / 2 + 4}
              textAnchor="end" fontSize="12"
              fill="currentColor"
            >
              {MONTH_NOM[mIdx]}
            </text>

            {/* słupek */}
            <rect
              x={leftPad} y={y}
              width={barW} height={barH}
              fill="var(--color-accent, #f97316)"
              rx="3"
            />

          </g>
        );
      })}

      {/* etykieta osi x */}
      <text
        x={leftPad + chartW / 2} y={svgH - 4}
        textAnchor="middle" fontSize="11"
        fill="currentColor" opacity="0.75"
      >
        kwota, zł
      </text>
    </svg>
  );
}

// ============================================================
// BLOK WYBORU – [A  B] lub [C  D] w stylu CKE
// ============================================================

function ChoiceBox({ labels }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        border: "1.5px solid currentColor",
        borderRadius: 4,
        padding: "1px 8px",
        margin: "0 4px",
        opacity: 0.75,
        verticalAlign: "middle",
        fontSize: "0.9em",
      }}
    >
      {labels.map((l, i) => (
        <React.Fragment key={l}>
          {i > 0 && <span style={{ opacity: 0.5 }}>|</span>}
          <strong>
            <InlineMath math={l} />
          </strong>
        </React.Fragment>
      ))}
    </span>
  );
}

// ============================================================
// TREŚĆ ZADANIA
// ============================================================

export function renderTask({ data }) {
  const {
    productNom, productGen, price,
    monthIndices, monthValues,
    q1Slots, q2HiSlot, q2LoSlot,
    q1Pct, q1Distractor,
    q2Pct, q2Distractor,
    q1CorrectFirst, q2CorrectFirst,
  } = data;

  const person = data.person || PERSONS[0];

  const q1MonthNoms = q1Slots.map((s) => MONTH_NOM[monthIndices[s]]);
  const q2HiMonthLoc = MONTH_LOC[monthIndices[q2HiSlot]];
  const q2LoMonthLoc = MONTH_LOC[monthIndices[q2LoSlot]];

  // opcje A/B i C/D
  const optA = q1CorrectFirst ? q1Pct : q1Distractor;
  const optB = q1CorrectFirst ? q1Distractor : q1Pct;
  const optC = q2CorrectFirst ? q2Pct : q2Distractor;
  const optD = q2CorrectFirst ? q2Distractor : q2Pct;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p style={{ marginBottom: 6 }}>
        {productNom} kosztuje <InlineMath math={`${price} \\text{ zł}`} />.
        {" "}Na diagramie przedstawiono kwoty, które {person.name}{" "}
        {person.verb} w kolejnych czterech miesiącach na zakup {productGen}.
      </p>

      <HorizontalBarChart monthIndices={monthIndices} monthValues={monthValues} />

      <p style={{ marginTop: 10, marginBottom: 10 }}>
        <strong>
          Uzupełnij zdania. Wybierz odpowiedź spośród oznaczonych literami{" "}
          <InlineMath math="A" /> i <InlineMath math="B" /> oraz odpowiedź
          spośród oznaczonych literami <InlineMath math="C" /> i{" "}
          <InlineMath math="D" />.
        </strong>
      </p>

      {/* Pytanie 1 */}
      <p style={{ marginBottom: 4 }}>
        W {q1MonthNoms[0]} i {q1MonthNoms[1]} łącznie {person.name}{" "}
        {person.verb} <ChoiceBox labels={["A", "B"]} /> kwoty potrzebnej na
        zakup {productGen}.
      </p>
      <p style={{ marginLeft: 16, marginBottom: 12 }}>
          <ChoiceOptionMath letter="A" math={`${optA}\\%`} />
        &emsp;&emsp;
          <ChoiceOptionMath letter="B" math={`${optB}\\%`} />
      </p>

      {/* Pytanie 2 */}
      <p style={{ marginBottom: 4 }}>
        W {q2HiMonthLoc} {person.name} {person.verb} kwotę o{" "}
        <ChoiceBox labels={["C", "D"]} /> większą od kwoty odłożonej w{" "}
        {q2LoMonthLoc}.
      </p>
      <p style={{ marginLeft: 16 }}>
          <ChoiceOptionMath letter="C" math={`${optC}\\%`} />
        &emsp;&emsp;
          <ChoiceOptionMath letter="D" math={`${optD}\\%`} />
      </p>
    </div>
  );
}

// ============================================================
// INPUT – tylko litery A/B i C/D (jak prawda/fałsz)
// ============================================================

export function renderInput(_task, userAnswer, onChange, disabled) {
  const ua = userAnswer || {};

  return (
    <div style={{ display: "grid", rowGap: 14, marginTop: 10 }}>
      <div>
        <div style={{ marginBottom: 4, fontSize: 13, opacity: 0.7 }}>
          Wybierz <InlineMath math="A" /> lub <InlineMath math="B" />:
        </div>
        <ABCDInput
          options={[
            { label: "A", textNode: <InlineMath math="A" /> },
            { label: "B", textNode: <InlineMath math="B" /> },
          ]}
          value={ua.q1 ?? null}
          onChange={(idx) => onChange({ ...ua, q1: idx })}
          disabled={disabled}
        />
      </div>

      <div>
        <div style={{ marginBottom: 4, fontSize: 13, opacity: 0.7 }}>
          Wybierz <InlineMath math="C" /> lub <InlineMath math="D" />:
        </div>
        <ABCDInput
          options={[
            { label: "C", textNode: <InlineMath math="C" /> },
            { label: "D", textNode: <InlineMath math="D" /> },
          ]}
          value={ua.q2 ?? null}
          onChange={(idx) => onChange({ ...ua, q2: idx })}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

// ============================================================
// CHECK – oba poprawne = 1 pkt
// ============================================================

export function checkAnswer({ data }, userAnswer) {
  const { q1CorrectFirst, q2CorrectFirst } = data;
  const ua = userAnswer || {};

  const q1Ok = ua.q1 != null && (q1CorrectFirst ? ua.q1 === 0 : ua.q1 === 1);
  const q2Ok = ua.q2 != null && (q2CorrectFirst ? ua.q2 === 0 : ua.q2 === 1);

  return {
    isCorrect: q1Ok && q2Ok,
    correct: q1Ok && q2Ok,
    score: q1Ok && q2Ok ? 1 : 0,
    maxScore: 1,
    parts: {
      "A/B – procent kwoty": q1Ok,
      "C/D – o ile % więcej": q2Ok,
    },
  };
}

// ============================================================
// POPRAWNA ODPOWIEDŹ
// ============================================================

export function renderAnswer({ data }) {
  const { q1CorrectFirst, q2CorrectFirst, q1Pct, q2Pct } = data;
  const q1Label = q1CorrectFirst ? "A" : "B";
  const q2Label = q2CorrectFirst ? "C" : "D";

  return (
    <div style={{ marginTop: 6, lineHeight: 1.9 }}>
      <div>
        <ChoiceOptionMath letter={q1Label} math={`${q1Pct}\\%`} />
      </div>
      <div>
        <ChoiceOptionMath letter={q2Label} math={`${q2Pct}\\%`} />
      </div>
    </div>
  );
}

// ============================================================
// ODPOWIEDŹ UŻYTKOWNIKA
// ============================================================

export function renderUserAnswer(userAnswer, { data }) {
  const ua = userAnswer || {};
  if (ua.q1 == null && ua.q2 == null) return <i>(brak odpowiedzi)</i>;

  const { q1CorrectFirst, q2CorrectFirst, q1Pct, q1Distractor, q2Pct, q2Distractor } = data;

  const q1Opts = q1CorrectFirst
    ? [q1Pct, q1Distractor]
    : [q1Distractor, q1Pct];
  const q2Opts = q2CorrectFirst
    ? [q2Pct, q2Distractor]
    : [q2Distractor, q2Pct];

  const q1Labels = ["A", "B"];
  const q2Labels = ["C", "D"];

  const q1Text = ua.q1 != null
        ? <ChoiceOptionMath letter={q1Labels[ua.q1]} math={`${q1Opts[ua.q1]}\\%`} />
    : <span>—</span>;
  const q2Text = ua.q2 != null
        ? <ChoiceOptionMath letter={q2Labels[ua.q2]} math={`${q2Opts[ua.q2]}\\%`} />
    : <span>—</span>;

  return (
    <div style={{ marginTop: 6, lineHeight: 1.9 }}>
      <div>Pytanie 1: {q1Text}</div>
      <div>Pytanie 2: {q2Text}</div>
    </div>
  );
}

// ============================================================
// WYJAŚNIENIE KROK PO KROKU
// ============================================================

export function renderExplanation({ data }) {
  const {
    price,
    monthIndices, monthValues,
    q1Slots, q2HiSlot, q2LoSlot,
    q1Pct, q2Pct,
    q1CorrectFirst, q2CorrectFirst,
  } = data;

  const q1MonthNoms = q1Slots.map((s) => MONTH_NOM[monthIndices[s]]);
  const q1Values = q1Slots.map((s) => monthValues[s]);
  const q1Sum = q1Values[0] + q1Values[1];

  const q2HiVal = monthValues[q2HiSlot];
  const q2LoVal = monthValues[q2LoSlot];
  const q2HiMonthLoc = MONTH_LOC[monthIndices[q2HiSlot]];
  const q2LoMonthLoc = MONTH_LOC[monthIndices[q2LoSlot]];

  const q1Label = q1CorrectFirst ? "A" : "B";
  const q2Label = q2CorrectFirst ? "C" : "D";

  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> Odczytaj z diagramu kwoty odłożone w{" "}
        {q1MonthNoms[0]} i {q1MonthNoms[1]}.
      </p>
      <p style={{ marginLeft: 16 }}>
        {q1MonthNoms[0]}: <InlineMath math={`${q1Values[0]} \\text{ zł}`} />,
        &ensp;
        {q1MonthNoms[1]}: <InlineMath math={`${q1Values[1]} \\text{ zł}`} />.
      </p>
      <p>
        Łącznie: <InlineMath math={`${q1Values[0]} + ${q1Values[1]} = ${q1Sum} \\text{ zł}`} />.
      </p>

      <p>
        <strong>Krok 2.</strong> Oblicz, jaki procent ceny (
        <InlineMath math={`${price} \\text{ zł}`} />) stanowi ta suma.
      </p>
      <p style={{ marginLeft: 16 }}>
        <InlineMath
          math={`\\frac{${q1Sum}}{${price}} \\cdot 100\\% = ${q1Pct}\\%`}
        />
      </p>
      <p>
          Odpowiedź: <ChoiceOptionMath letter={q1Label} math={`${q1Pct}\\%`} />
      </p>

      <p style={{ marginTop: 14 }}>
        <strong>Krok 3.</strong> Odczytaj kwoty odłożone w {q2HiMonthLoc} i{" "}
        {q2LoMonthLoc}.
      </p>
      <p style={{ marginLeft: 16 }}>
        {q2HiMonthLoc}: <InlineMath math={`${q2HiVal} \\text{ zł}`} />,
        &ensp;
        {q2LoMonthLoc}: <InlineMath math={`${q2LoVal} \\text{ zł}`} />.
      </p>

      <p>
        <strong>Krok 4.</strong> Oblicz, o ile procent kwota z {q2HiMonthLoc}{" "}
        jest większa od kwoty z {q2LoMonthLoc}.
      </p>
      <p style={{ marginLeft: 16 }}>
        <InlineMath
          math={`\\frac{${q2HiVal} - ${q2LoVal}}{${q2LoVal}} \\cdot 100\\% = \\frac{${q2HiVal - q2LoVal}}{${q2LoVal}} \\cdot 100\\% = ${q2Pct}\\%`}
        />
      </p>
      <p>
          Odpowiedź: <ChoiceOptionMath letter={q2Label} math={`${q2Pct}\\%`} />
      </p>
      <p style={{ marginTop: 14 }}>
        <strong>Krok 5.</strong> Zapisujemy komplet odpowiedzi:{" "}
        <ChoiceOptionMath letter={q1Label} math={`${q1Pct}\\%`} />
        {", "}
        <ChoiceOptionMath letter={q2Label} math={`${q2Pct}\\%`} />
        .
      </p>
    </div>
  );
}
